if (!customElements.get('custom-product-tabs')) {
  class CustomProductTabs extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        tabHeader: '[role="tablist"]',
        tabPanels: ['[role="tabpanel"]'],
        tabNavs: ['[role="tab"]'],
        productsList: '.products-list',
        carouselWrapper: '.custom-product-tabs__carousel',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.domNodes = FoxTheme.utils.queryDomNodes(this.selectors, this);

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      
      this.enableCarousel = this.dataset.enableCarousel === 'true';
      this.columnsDesktop = parseInt(this.dataset.columnsDesktop);
      this.columnsTablet = parseInt(this.dataset.columnsTablet);

      this.selectedIndex = 0;
      this.selectedTab = this.domNodes.tabPanels[this.selectedIndex];
      this.scrollToActiveTab = this.dataset.scrollToActiveTab === 'true';
      this.scrollOffset = 20;
      this.headerEl = document.querySelector('.header-section header[is="sticky-header"]');

      // Store carousel instances for each tab
      this.carouselInstances = {};

      this.init();
      this.setActiveTab(0);

      // Listen for viewport changes
      if (this.enableCarousel) {
        document.addEventListener('matchMobile', () => {
          this.handleViewportChange();
        });
        document.addEventListener('unmatchMobile', () => {
          this.handleViewportChange();
        });
      }
    }

    init() {
      this.domNodes.tabNavs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick.bind(this));
      });
      this.domNodes.tabHeader && this.domNodes.tabHeader.addEventListener('keydown', this.handleKeyDown.bind(this));
      this.setAccessible();

      // Initialize carousel for first tab if enabled
      if (this.enableCarousel) {
        this.initCarouselForTab(this.selectedTab);
      }
    }

    handleViewportChange() {
      if (FoxTheme.config.mqlMobile) {
        // Switched to mobile - destroy all carousels
        this.destroyAllCarousels();
      } else {
        // Switched to desktop - reinitialize carousel for active tab
        this.initCarouselForTab(this.selectedTab);
      }
    }

    static get observedAttributes() {
      return ['data-selected'];
    }

    get buttons() {
      return (this._buttons = this._buttons || Array.from(this.querySelectorAll('button[role="tab"]')));
    }

    getHeaderHeight() {
      let headerHeight = 0;

      if (this.headerEl) {
        const height = Math.round(this.headerEl.offsetHeight);
        if (this.headerEl.isAlwaysSticky) {
          headerHeight = height;
        } else {
          const isSticky = document.body.classList.contains(this.headerEl.classes.pinned);

          if (window.scrollY > this.offsetTop - this.scrollOffset) {
            headerHeight = height;
          }

          if (isSticky && window.scrollY >= this.offsetTop - this.scrollOffset - height) {
            headerHeight = height;
          }
        }
      }

      return headerHeight;
    }

    scrollToTop() {
      let offsetTop = this.offsetTop - this.scrollOffset - this.getHeaderHeight();
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }

    onTabClick(evt) {
      if (evt.target.tagName === 'A' && evt.target.hasAttribute('href')) return;
      evt.preventDefault();
      const currentTab = evt.currentTarget;
      const index = currentTab ? Number(currentTab.dataset.index) : 0;
      this.setActiveTab(index);

      if (this.scrollToActiveTab) {
        this.scrollToTop();
      }
    }

    setActiveTab(tabIndex) {
      const { tabNavs, tabPanels } = this.domNodes;
      if (tabIndex !== -1) {
        const newHeader = tabNavs && tabNavs[tabIndex];
        const newTab = tabPanels && tabPanels[tabIndex];
        this.setAttribute('data-selected', tabIndex);

        tabNavs.forEach((nav) => nav.setAttribute('aria-selected', false));

        newHeader && newHeader.setAttribute('aria-selected', true);

        this.selectedIndex = tabIndex;
        this.selectedTab = newTab;

        this.onTabChange();

        this.dispatchEvent(
          new CustomEvent('tabChange', {
            bubbles: true,
            detail: { selectedIndex: tabIndex, selectedTab: newTab },
          })
        );
      }
    }

    onTabChange() {
      const productsList = this.selectedTab.querySelector(this.selectors.productsList);
      if (productsList) {
        // Products already loaded, check if carousel needs initialization
        if (this.enableCarousel && !FoxTheme.config.mqlMobile) {
          this.initCarouselForTab(this.selectedTab);
        }
        return;
      }

      const template = this.selectedTab.querySelector(`template`);
      const tabContent = template.content.cloneNode(true).firstElementChild;
      this.selectedTab.appendChild(tabContent);

      // Initialize carousel for newly loaded tab
      if (this.enableCarousel && !FoxTheme.config.mqlMobile) {
        this.initCarouselForTab(this.selectedTab);
      }
    }

    initCarouselForTab(tabPanel) {
      const tabId = tabPanel.id;
      
      // Check if carousel already initialized for this tab
      if (this.carouselInstances[tabId]) {
        return;
      }

      // Only initialize carousel on desktop
      if (FoxTheme.config.mqlMobile) {
        return;
      }

      const productCount = parseInt(tabPanel.dataset.productCount);
      
      // Only initialize if products > columns
      if (productCount <= this.columnsDesktop) {
        return;
      }

      const carouselWrapper = tabPanel.querySelector(this.selectors.carouselWrapper);
      const productsList = tabPanel.querySelector(this.selectors.productsList);

      if (!carouselWrapper || !productsList) {
        return;
      }

      // Get column gap from CSS custom properties
      const columnGap = FoxTheme.utils.getGridColumnGap(productsList);

      const sliderOptions = {
        slidesPerView: parseInt(this.columnsTablet),
        spaceBetween: columnGap.tablet,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        breakpoints: {
          1024: {
            slidesPerView: parseInt(this.columnsTablet),
            spaceBetween: columnGap.tabletLarge,
          },
          1280: {
            slidesPerView: parseInt(this.columnsDesktop),
            spaceBetween: columnGap.desktop,
          },
        },
        loop: true,
        threshold: 2,
      };

      // Transform grid to swiper structure
      carouselWrapper.classList.add(this.classes.swiper);
      productsList.classList.remove(this.classes.grid);
      productsList.classList.add(this.classes.swiperWrapper);

      // Initialize Swiper
      const carouselInstance = new window.FoxTheme.Carousel(carouselWrapper, sliderOptions);
      carouselInstance.init();

      // Store instance
      this.carouselInstances[tabId] = carouselInstance;

      // Handle focus for accessibility
      const focusableElements = FoxTheme.a11y.getFocusableElements(carouselWrapper);
      focusableElements.forEach((element) => {
        element.addEventListener('focusin', () => {
          const slide = element.closest('.swiper-slide');
          carouselInstance.slider.slideTo(carouselInstance.slider.slides.indexOf(slide));
        });
      });

      // Shopify theme editor support
      if (Shopify.designMode && typeof carouselInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);

          carouselInstance.slider.slideToLoop(index);
        });
      }
    }

    destroyCarouselForTab(tabPanel) {
      const tabId = tabPanel.id;
      const carouselInstance = this.carouselInstances[tabId];

      if (!carouselInstance) {
        return;
      }

      const carouselWrapper = tabPanel.querySelector(this.selectors.carouselWrapper);
      const productsList = tabPanel.querySelector(this.selectors.productsList);

      if (carouselWrapper && productsList) {
        carouselWrapper.classList.remove(this.classes.swiper);
        productsList.classList.remove(this.classes.swiperWrapper);
        productsList.classList.add(this.classes.grid);

        if (typeof carouselInstance === 'object') {
          carouselInstance.slider.destroy();
          delete this.carouselInstances[tabId];
        }
      }
    }

    destroyAllCarousels() {
      const { tabPanels } = this.domNodes;
      tabPanels.forEach((tabPanel) => {
        this.destroyCarouselForTab(tabPanel);
      });
    }

    setAccessible() {
      const { tabNavs, tabPanels } = this.domNodes;
      tabNavs.forEach((tab, index) => {
        if (tab.id) tabPanels[index].setAttribute('aria-labelledby', tab.id);
        tab.setAttribute('aria-selected', index === 0);
        tab.setAttribute('data-index', index);
      });
      tabPanels.forEach((panel, index) => {
        if (panel.id) tabNavs[index].setAttribute('aria-controls', panel.id);
        panel.setAttribute('tabindex', 0);
      });
    }

    handleKeyDown(e) {
      const { tabNavs } = this.domNodes;
      if (e.keyCode === 39 || e.keyCode === 37) {
        tabNavs[this.selectedIndex].setAttribute('tabindex', -1);
        if (e.keyCode === 39) {
          this.selectedIndex++;
          if (this.selectedIndex >= tabNavs.length) {
            this.selectedIndex = 0;
          }
        } else if (e.keyCode === 37) {
          this.selectedIndex--;
          if (this.selectedIndex < 0) {
            this.selectedIndex = tabNavs.length - 1;
          }
        }

        tabNavs[this.selectedIndex].setAttribute('tabindex', 0);
        tabNavs[this.selectedIndex].focus();
      }
    }

    getSelected() {
      return {
        index: this.selectedIndex,
        element: this.selectedTab,
      };
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'data-selected' && oldValue !== null && oldValue !== newValue) {
        const fromButton = this.buttons[parseInt(oldValue)];
        const toButton = this.buttons[parseInt(newValue)];
        this.transition(
          document.getElementById(fromButton.getAttribute('aria-controls')),
          document.getElementById(toButton.getAttribute('aria-controls'))
        );
      }
    }

    async transition(fromPanel, toPanel) {
      await FoxTheme.Motion.animate(
        fromPanel,
        { transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] },
        { duration: 0.3 }
      ).finished;

      fromPanel.hidden = true;
      toPanel.hidden = false;

      FoxTheme.Motion.animate(
        toPanel,
        { transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] },
        { duration: 0.3 }
      ).finished;

      toPanel.querySelector('grid-list') && toPanel.querySelector('grid-list').showGridItems();
    }
  }
  customElements.define('custom-product-tabs', CustomProductTabs);
}
